import React, { useEffect, useState } from 'react';
import { useToast } from '../../../components/ToastContext';
import { loadKept, keepNumber, missingNumbers, claimSoftFiles, downloadText, fileNames } from './claimsoftFiles';
import { finishJob } from './orderJobs';
import ListGrid from '../../../components/ListGrid';
import { Section } from './panelParts';

/*
  THE NUMBERS FROM ACCOUNT EDGE, AND THE FILES BACK TO CLAIMSOFT. Ordering
  stage 6, 12 October 2026.

  His words of 4 October: *"a place to input these TO and PO numbers as i go and
  then would need to tell OnHand that I was done with that input."* So: a row per
  customer sale, each number saved the moment he leaves the box, over however
  long Account Edge takes; then **I'm done in Account Edge**, which checks every
  number is in, gives the two ClaimSoft files in the order they must be
  imported, and marks the job Finished, which takes it out of the queue.

  On a finished job (`finished`), the same component offers the two files again.
*/
export default function ClaimSoftReturn({ job, lines, sales, userId, finished, onFinished }) {
  const toast = useToast();
  const [kept, setKept] = useState(null);
  const [failed, setFailed] = useState(null);
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    loadKept(job.id).then(r => {
      if (!live) return;
      if (r.error) { setFailed(r.error.message); return; }
      setKept(r.kept);
    });
    return () => { live = false; };
  }, [job.id]);

  if (failed) return <p style={{ color: 'var(--critical-ink)' }}>The numbers could not be read just now: {failed}</p>;
  if (!kept) return null;

  const save = async (sale, field, value) => {
    const had = (kept.get(sale.sale_key) || {})[field] || '';
    if (String(value).trim() === String(had).trim()) return;
    const r = await keepNumber({ jobId: job.id, sale, field, value, userId });
    if (!r.ok) { toast.error(r.error.message); return; }
    setKept(m => new Map(m).set(sale.sale_key, { ...(m.get(sale.sale_key) || {}), ...r.row }));
  };

  const files = () => claimSoftFiles({ lines, sales, kept });
  const names = fileNames(job);
  const giveFiles = f => {
    if (f.po) downloadText(f.po, names.po);
    if (f.to) setTimeout(() => downloadText(f.to, names.to), 400);
  };

  const done = async () => {
    setProblem(null);
    setBusy(true);
    /* Read again from the database, so a number still saving from the box he
       has just left is counted, and what goes into the files is what is kept. */
    const fresh = await loadKept(job.id);
    if (fresh.error) { setBusy(false); toast.error(fresh.error.message); return; }
    setKept(fresh.kept);
    const missing = missingNumbers(sales, fresh.kept);
    if (missing.length) { setBusy(false); setProblem('Still to type: ' + missing.join(', ') + '.'); return; }
    const f = claimSoftFiles({ lines, sales, kept: fresh.kept });
    giveFiles(f);
    const r = await finishJob(job.id);
    setBusy(false);
    if (!r.ok) { toast.error(r.error.message); return; }
    toast.success(job.reference + ' is finished. Import the PO file first, then the TO file.');
    onFinished && onFinished();
  };

  if (finished) {
    const f = files();
    return (
      <Section title="The ClaimSoft files" note="Import the PO file first, then the TO file: ClaimSoft matches the PO file on the original transfer order number, which the TO file replaces.">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {f.po && <button type="button" className="btn btn-quiet" onClick={() => downloadText(f.po, names.po)}>1. PO file ({f.poLines} line{f.poLines === 1 ? '' : 's'})</button>}
          {f.to && <button type="button" className="btn btn-quiet" onClick={() => downloadText(f.to, names.to)}>2. TO file ({f.toLines} line{f.toLines === 1 ? '' : 's'})</button>}
        </div>
      </Section>
    );
  }

  /* Drawn on the house pieces from 12 October 2026, with the run. */
  return (
    <Section title="The numbers from Account Edge" note="As you make each purchase order in Account Edge, type its number here. Each number is kept as soon as you leave the box.">
      <ListGrid
        columns={[
          { key: 's', label: 'Customer sale', width: 'minmax(200px, 1.4fr)' },
          { key: 'c', label: 'Customer sale number', width: '200px' },
          { key: 'p', label: 'PO number', width: '200px' },
        ]}
        rows={sales}
        keyOf={x => x.sale_key}
        cells={x => {
          const k = kept.get(x.sale_key) || {};
          return [
            <span style={{ fontSize: 15, fontWeight: 700 }}>{sales.indexOf(x) + 1}. {x.austin ? 'From Austin' : x.name}
              <span style={{ display: 'block', fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 3 }}>{x.austin ? 'Its number is the new TO number' : 'The sale number is optional'}</span>
            </span>,
            <input className="fld" style={{ width: '100%' }} defaultValue={k.sale_number || ''} aria-label={'Customer sale number for ' + (x.austin ? 'Austin' : x.name)}
              onBlur={e => save(x, 'sale_number', e.target.value)} />,
            x.austin ? <span style={{ color: 'var(--text-secondary)' }}>None: Austin sends it</span> : (
              <input className="fld" style={{ width: '100%' }} defaultValue={k.po_number || ''} aria-label={'PO number for ' + x.name}
                onBlur={e => save(x, 'po_number', e.target.value)} />
            ),
          ];
        }}
      />
      {problem && <div style={{ fontSize: 14, padding: '10px 14px', borderRadius: 12, background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>{problem}</div>}
      <div className="ua-actions" style={{ justifyContent: 'flex-end' }}>
        <span style={{ marginRight: 'auto', fontSize: 14, color: 'var(--text-secondary)' }}>Gives you the two ClaimSoft files, the PO file first, and marks the job Finished.</span>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={done}>{busy ? 'Finishing...' : "I'm done in Account Edge"}</button>
      </div>
    </Section>
  );
}
